import {
  ArrowForward,
  ChevronLeft,
  ChevronRight,
  Close,
  PauseRounded,
  PlayArrowRounded,
  Refresh,
} from '@mui/icons-material';
import { Box, DialogContent, IconButton, Typography } from '@mui/material';
import * as R from 'ramda';
import { useState } from 'react';

import { AnswerBox, Button, ButtonVariants } from '@/components';
import { ComplianceTypes } from '@/constants';
import { EpisodeCompleted, PostQuizAssessmentRecs } from '@/containers';
import { AnalyticsService } from '@/services';
import { QuizAnswer } from '@/types';
import { convertSecondsToMinutes } from '@/utils';

import { chapterQuizModalStyles } from './styles';
import { ChapterQuizModalContentProps } from './types';

export const ChapterQuizModalContentComponent = ({
  onNextPage,
  onPreviousPage,
  onSelectAnswer,
  onClearAnswer,
  onSubmitQuiz,
  onPlayChapter,
  onClose,
  showAssessment,
  pages,
  selectedPageIndex,
  selectedAnswerIndex,
  isChapterCompleted,
  isChapterPlaying,
  isQuizCompleted,
  isSubmitLoading,
  submission,
  compliance,
  isOnCompletionStep,
  episodeId,
}: ChapterQuizModalContentProps) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const selectedPage = pages[selectedPageIndex];
  const { chapter, chapterIndex, quiz, quizCountLabel } = selectedPage;

  const isCPE = compliance === ComplianceTypes.CPE;
  const chapterDuration = convertSecondsToMinutes(chapter.duration);

  const episodeDuration = convertSecondsToMinutes(
    pages.reduce((total, page) => total + page.chapter.duration, 0),
  );

  if (isOnCompletionStep) {
    return isCPE ? (
      <Box sx={chapterQuizModalStyles.modalContainer}>
        <Box sx={styles.headerContainer}>
          <Typography variant="h1" color="text.main" sx={styles.headerLabel}>
            Quizzes Complete
          </Typography>
          <IconButton aria-label="Close modal" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={styles.summaryContainer}>
          <Box sx={styles.summaryContent}>
            <EpisodeCompleted episodeId={episodeId} />
            <Typography variant="h1">You&apos;re almost done!</Typography>
            <Typography variant="body2" sx={styles.summaryAssessment}>
              You&apos;ll need to complete the Qualified Assessment to earn your
              CPE credits.
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.summaryComplete}>
          <Button
            onClick={onClose}
            label="Later"
            variant="tertiary"
            fullWidth={false}
            skinny={false}
          />
          <Button
            onClick={showAssessment}
            label="Take Assessment"
            fullWidth={false}
            skinny={false}
          />
        </Box>
      </Box>
    ) : (
      <Box>
        <IconButton
          aria-label="close chapter modal"
          onClick={() => {
            AnalyticsService.closePostQuizRecommendations();
            onClose();
          }}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            color: 'text.contrast',
          }}>
          <Close />
        </IconButton>
        <PostQuizAssessmentRecs episodeDuration={episodeDuration} />
      </Box>
    );
  }

  return (
    <DialogContent sx={chapterQuizModalStyles.modalContainer}>
      <Box>
        <Box sx={styles.headerContainer}>
          <Box sx={styles.headerLabel}>
            <Typography variant="h1" color="text.main">
              Chapter {chapterIndex + 1}
            </Typography>
            <IconButton
              onClick={onPlayChapter}
              aria-label="Play"
              size="small"
              sx={styles.playButton}>
              {isChapterPlaying ? <PauseRounded /> : <PlayArrowRounded />}
            </IconButton>
          </Box>
          <Box sx={styles.headerActions}>
            <IconButton
              aria-label="Previous"
              onClick={() => {
                setIsSubmitted(false);
                onPreviousPage();
              }}
              sx={[styles.headerAction, styles.headerActionResponsive]}
              disabled={selectedPageIndex === 0}>
              <ChevronLeft />
            </IconButton>
            <IconButton
              aria-label="Next"
              onClick={() => {
                setIsSubmitted(false);
                onNextPage();
              }}
              sx={[styles.headerAction, styles.headerActionResponsive]}
              disabled={selectedPageIndex === pages.length - 1}>
              <ChevronRight />
            </IconButton>
            <IconButton aria-label="Close modal" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="h3" color="text.main">
          {`${chapterDuration} min${chapterDuration > 1 ? 's' : ''}`}
        </Typography>
      </Box>
      <Box>
        <Typography variant="h4" color="text.main">
          {chapter.name}
        </Typography>
        <Typography variant="body2" color="text.main">
          {chapter.description}
        </Typography>
      </Box>
      {!!quiz && (
        <>
          <Box>
            <Typography variant="h2" color="text.main">
              {!quizCountLabel ? 'Quiz' : `Quizzes (${quizCountLabel})`}
            </Typography>
            {isChapterCompleted ? (
              <Typography variant="body2" color="text.main">
                {quiz.question}
              </Typography>
            ) : (
              <Box sx={styles.pendingQuizContainer}>
                <Typography variant="body2" color="text.main">
                  Available when chapter is completed
                </Typography>
              </Box>
            )}
          </Box>
          {isChapterCompleted && (
            <Box>
              <Box sx={styles.answerGrid}>
                {quiz.answers.map((answer: QuizAnswer, index: number) => (
                  <Box sx={styles.answerContainer} key={index}>
                    <AnswerBox
                      onSelect={() => {
                        setIsSubmitted(false);
                        onSelectAnswer(index);
                      }}
                      isSelected={selectedAnswerIndex === index}
                      isCorrect={
                        (submission?.answerIndex === index &&
                          submission?.isCorrect === true &&
                          selectedAnswerIndex === index) ||
                        (isQuizCompleted && answer.isCorrect)
                      }
                      isIncorrect={
                        submission?.answerIndex === index &&
                        submission?.isCorrect === false &&
                        selectedAnswerIndex === index
                      }
                      answer={answer}
                      index={index}
                      questionId={quiz.quizId}
                      isDisabled={isQuizCompleted || isSubmitLoading}
                      showCaption={isQuizCompleted && isCPE}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
      <Box
        sx={[
          styles.bottomRow,
          isSubmitted &&
            (isQuizCompleted
              ? styles.bottomRowSuccess
              : styles.bottomRowFailure),
        ]}>
        {isQuizCompleted ? (
          isSubmitted ? (
            <>
              <Box sx={styles.bottomRowMessage}>
                <Typography variant="h4" color="text.main">
                  Well done!
                </Typography>
                {!isCPE && (
                  <Typography variant="body2" color="text.main">
                    You’ve earned {chapterDuration} minutes of CPD!
                  </Typography>
                )}
              </Box>
              <Button
                onClick={() => {
                  setIsSubmitted(false);
                  onNextPage();
                }}
                label="Continue"
                endIcon={<ArrowForward />}
                variant={ButtonVariants.SUCCESS}
                fullWidth={false}
                skinny={false}
              />
            </>
          ) : (
            <Button
              onClick={() => {
                setIsSubmitted(false);
                onNextPage();
              }}
              label="Continue"
              endIcon={<ArrowForward />}
              variant={ButtonVariants.PRIMARY}
              fullWidth={false}
              skinny={false}
              sx={
                selectedPageIndex === pages.length - 1 && !isSubmitted
                  ? {
                      visibility: 'hidden',
                    }
                  : {}
              }
            />
          )
        ) : isSubmitted ? (
          <>
            <Box>
              <Typography variant="h4" color="text.main">
                Sorry, wrong answer.
              </Typography>
              <Typography variant="body2" color="text.main">
                Please try again
              </Typography>
            </Box>
            <Button
              onClick={() => {
                onClearAnswer();
                setIsSubmitted(false);
              }}
              label="Retry"
              variant={ButtonVariants.FAILURE}
              startIcon={<Refresh />}
              fullWidth={false}
              skinny={false}
            />
          </>
        ) : (
          <Button
            onClick={async () => {
              const quizSubmittedSuccessfully = await onSubmitQuiz();
              if (quizSubmittedSuccessfully) setIsSubmitted(true);
            }}
            label="Submit"
            isLoading={isSubmitLoading}
            disabled={
              !isChapterCompleted ||
              R.isNil(selectedAnswerIndex) ||
              isSubmitLoading
            }
            variant={ButtonVariants.PRIMARY}
            fullWidth={false}
            skinny={false}
          />
        )}
      </Box>
    </DialogContent>
  );
};

const styles = {
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  headerLabel: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
  },
  headerActions: {
    gap: 1,
    display: 'flex',
  },
  headerAction: {
    backgroundColor: 'grey50',
    color: 'text.main',
  },
  headerActionResponsive: {
    display: {
      xs: 'none',
      sm: 'flex',
    },
  },
  summaryContainer: {
    display: 'flex',
    gap: 2,
    my: 4,
    flexDirection: { sm: 'row', xs: 'column' },
  },
  summaryContent: {
    flexGrow: 1,
    alignContent: 'center',
    textAlign: 'center',
  },
  summaryAssessment: {
    textAlign: 'center',
    justifySelf: 'center',
    maxWidth: 350,
    my: 2,
  },
  summaryComplete: {
    display: 'flex',
    gap: 2,
    flexDirection: { sm: 'row', xs: 'column' },
    justifyContent: 'end',
    p: 2,
  },
  pendingQuizContainer: {
    height: 240,
    display: 'flex',
    border: '1px solid',
    borderColor: 'grey100',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 3,
    mt: 2,
  },
  answerGrid: {
    display: 'grid',
    gridTemplateColumns: { md: '1fr 1fr' },
    gap: 2,
  },
  answerContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'end',
    gap: 3,
    mx: {
      xs: -4,
      sm: -6,
    },
    mb: -2.5,
    py: 2,
    px: {
      xs: 4,
      sm: 6,
    },
  },
  bottomRowMessage: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  bottomRowSuccess: {
    backgroundColor: 'green20',
  },
  bottomRowFailure: {
    backgroundColor: 'red10',
  },
  playButton: {
    border: 1.5,
    borderColor: 'secondary.main',
    color: 'secondary.main',
    ml: 2,
  },
};
